import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Car, LayoutGrid, Users, Home, Navigation, FileText,
  Palette, Search, Image, Settings, LogOut, Menu, X, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/inventory", label: "Inventory", icon: Car, testid: "admin-nav-inventory" },
  { to: "/admin/categories", label: "Categories", icon: LayoutGrid, testid: "admin-nav-categories" },
  { to: "/admin/leads", label: "Leads", icon: Users, testid: "admin-nav-leads" },
  { group: "Website" },
  { to: "/admin/website/homepage", label: "Homepage", icon: Home, testid: "admin-nav-homepage" },
  { to: "/admin/website/navigation", label: "Navigation & Footer", icon: Navigation, testid: "admin-nav-navigation" },
  { to: "/admin/website/pages", label: "Pages", icon: FileText, testid: "admin-nav-pages" },
  { to: "/admin/website/design", label: "Design", icon: Palette, testid: "admin-nav-design" },
  { to: "/admin/website/seo", label: "SEO", icon: Search, testid: "admin-nav-seo" },
  { group: "Content" },
  { to: "/admin/media", label: "Media Library", icon: Image, testid: "admin-nav-media" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const navContent = (
    <>
      {NAV.map((item, i) =>
        item.group ? (
          <p key={`g-${i}`} className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 px-4 pt-5 pb-2">
            {item.group}
          </p>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-[#FF6B00]/10 text-[#FF6B00] border-r-2 border-[#FF6B00]" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
              }`
            }
            data-testid={item.testid}
          >
            <item.icon className="h-4 w-4 shrink-0" /> {item.label}
          </NavLink>
        )
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[#F4F5F7] text-zinc-900 flex" data-testid="admin-layout">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col bg-white border-r border-zinc-200 fixed inset-y-0 z-40">
        <div className="h-16 flex items-center px-4 border-b border-zinc-200">
          <span className="font-display text-lg font-black text-zinc-900">
            SSH <span className="text-[#FF6B00]">ADMIN</span>
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto py-3" aria-label="Admin navigation">{navContent}</nav>
        <div className="border-t border-zinc-200 p-3 space-y-1">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm text-zinc-500 hover:text-zinc-900" data-testid="admin-view-site">
            <ExternalLink className="h-4 w-4" /> View Website
          </a>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-500 hover:text-red-600" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
          <p className="px-4 pt-1 text-[10px] text-zinc-500 truncate" data-testid="admin-user-email">{user?.email}</p>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4">
        <span className="font-display text-base font-black text-zinc-900">
          SSH <span className="text-[#FF6B00]">ADMIN</span>
        </span>
        <button onClick={() => setOpen(!open)} className="text-zinc-600 p-2" aria-label="Toggle admin menu" data-testid="admin-mobile-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setOpen(false)}>
          <nav className="absolute left-0 top-14 bottom-0 w-64 bg-white border-r border-zinc-200 overflow-y-auto py-3" onClick={(e) => e.stopPropagation()} aria-label="Admin mobile navigation">
            {navContent}
            <div className="border-t border-zinc-200 mt-3 p-3">
              <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-500 hover:text-red-600" data-testid="admin-logout-mobile">
                <LogOut className="h-4 w-4" /> Sign Out
              </button>
            </div>
          </nav>
        </div>
      )}

      <main className="flex-1 lg:ml-60 pt-14 lg:pt-0 min-w-0">
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
